import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  attendanceTodayUrl, attendanceDevicesUrl, attendanceUnlinkedUrl,
  attendanceLinkUrl, hrEmployeesUrl, branchesUrl,
} from '../../BaseURL';
import { fmtDate, fmtNumber, titleCase } from '../../utils/format';
import { toastSuccess, showAxiosError } from '../../utils/swal';

const EVENT_LABEL = {
  0: '🟢 Check in',
  1: '🔴 Check out',
  2: '☕ Break out',
  3: '🔄 Break in',
  4: '➕ Overtime in',
  5: '➖ Overtime out',
};
const VERIFY_LABEL = { 1: 'Fingerprint', 4: 'Card', 15: 'Face' };

const fmt12 = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
};

const diffHours = (a, b) => {
  if (!a || !b) return null;
  const mins = (new Date(b) - new Date(a)) / 60000;
  if (mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
};

const Attendance = () => {
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const isManager = me?.role === 'super_admin' || me?.role === 'branch_manager';

  const [date, setDate]       = useState(() => new Date().toISOString().slice(0, 10));
  const [scans, setScans]     = useState([]);
  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('summary'); // 'summary' | 'live' | 'unlinked' | 'devices'

  const load = async () => {
    setLoading(true);
    try {
      const [t, d, u, emp, br] = await Promise.allSettled([
        axios.get(attendanceTodayUrl,    { params: { date } }),
        axios.get(attendanceDevicesUrl),
        axios.get(attendanceUnlinkedUrl),
        axios.get(hrEmployeesUrl,        { params: { active: 1 } }),
        axios.get(branchesUrl),
      ]);
      if (t.status   === 'fulfilled') setScans(t.value.data?.results     || []);
      if (d.status   === 'fulfilled') setDevices(d.value.data?.results   || []);
      if (u.status   === 'fulfilled') setUnlinked(u.value.data?.results  || []);
      if (emp.status === 'fulfilled') setEmployees(emp.value.data?.results || []);
      if (br.status  === 'fulfilled') setBranches(br.value.data?.results  || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [date]);
  useEffect(() => {
    if (tab !== 'live' && tab !== 'summary') return undefined;
    const iv = setInterval(() => load().catch(() => {}), 20000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [tab, date]);

  // Build per-person summary: group scans by employee_id (or device_user_id fallback)
  const summary = useMemo(() => {
    const map = new Map();
    for (const s of scans) {
      const key = s.employee_id ? `emp:${s.employee_id}` : s.user_id ? `usr:${s.user_id}` : `dev:${s.device_serial}:${s.device_user_id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          name:       s.employee_name || s.user_name || s.display_name || null,
          department: s.department    || null,
          position:   s.position      || null,
          branch:     s.branch_name   || null,
          device_user_id: s.device_user_id,
          first_scan: s.event_time,
          last_scan:  s.event_time,
          check_in:   s.event_type === 0 ? s.event_time : null,
          check_out:  s.event_type === 1 ? s.event_time : null,
          scans:      0,
          linked:     !!(s.employee_id || s.user_id),
        });
      }
      const row = map.get(key);
      row.scans++;
      if (s.event_time < row.first_scan) row.first_scan = s.event_time;
      if (s.event_time > row.last_scan)  row.last_scan  = s.event_time;
      // track earliest check-in and latest check-out
      if (s.event_type === 0) {
        if (!row.check_in || s.event_time < row.check_in) row.check_in = s.event_time;
      }
      if (s.event_type === 1) {
        if (!row.check_out || s.event_time > row.check_out) row.check_out = s.event_time;
      }
    }
    return [...map.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [scans]);

  const stats = useMemo(() => {
    const linked   = new Set(scans.filter((s) => s.employee_id || s.user_id).map((s) => s.employee_id ? `emp:${s.employee_id}` : `usr:${s.user_id}`));
    const unknown  = scans.filter((s) => !s.employee_id && !s.user_id).length;
    const online   = devices.filter((d) => d.last_seen_at && (Date.now() - new Date(d.last_seen_at).getTime()) < 5 * 60 * 1000).length;
    return { total: scans.length, people: linked.size, unknown, online, devTotal: devices.length };
  }, [scans, devices]);

  return (
    <>
      <div className="kpi-row">
        <KpiCard label="Scans today"    val={fmtNumber(stats.total)}   hint={`on ${date}`}                     tone="brand"   icon="👉" />
        <KpiCard label="People on site" val={fmtNumber(stats.people)}  hint="with registered employee ID"       tone="success" icon="👥" />
        <KpiCard label="Unlinked scans" val={fmtNumber(stats.unknown)} hint="device ID not tied to an employee" tone="warn"    icon="⚠️" />
        <KpiCard label="Devices online" val={`${stats.online} / ${stats.devTotal}`} hint="seen in last 5 min"  tone="accent"  icon="🛰️" />
      </div>

      <div className="panel">
        <div className="panel-head">
          <div className="head-text">
            <h2>Attendance</h2>
            <span className="sub">Live fingerprint punches — register employees in <strong>Employees</strong> to see their names here.</span>
          </div>
          <div className="head-actions">
            <input type="date" className="f-input" value={date} onChange={(e) => setDate(e.target.value)} style={{ maxWidth: 160 }} />
            <button className="btn-ghost" onClick={() => load()}>🔄 Refresh</button>
          </div>
        </div>

        <div className="segmented" style={{ marginBottom: 14 }}>
          <button className={`seg ${tab === 'summary'  ? 'active' : ''}`} onClick={() => setTab('summary')}>📋 Summary <Chip val={summary.length} /></button>
          <button className={`seg ${tab === 'live'     ? 'active' : ''}`} onClick={() => setTab('live')}>🕒 All scans <Chip val={scans.length} /></button>
          <button className={`seg ${tab === 'unlinked' ? 'active' : ''}`} onClick={() => setTab('unlinked')}>🔗 Link scans <Chip val={unlinked.length} warn /></button>
          <button className={`seg ${tab === 'devices'  ? 'active' : ''}`} onClick={() => setTab('devices')}>🛰️ Devices <Chip val={devices.length} /></button>
        </div>

        {loading ? (
          <div className="empty-state"><div className="ic">⏳</div><h4>Loading…</h4></div>
        ) : tab === 'summary' ? (
          <SummaryTable rows={summary} date={date} />
        ) : tab === 'live' ? (
          <ScansTable rows={scans} />
        ) : tab === 'unlinked' ? (
          <UnlinkedPanel rows={unlinked} employees={employees} onLinked={load} disabled={!isManager} />
        ) : (
          <DevicesPanel rows={devices} onRefresh={load} branches={branches} isManager={isManager} />
        )}
      </div>
    </>
  );
};

// ─── Summary table (one row per employee / device ID) ─────────
const SummaryTable = ({ rows, date }) => (
  rows.length === 0 ? (
    <div className="empty-state">
      <div className="ic">📋</div>
      <h4>No attendance recorded yet for {date}</h4>
      <p>Once someone scans on a fingerprint terminal their name will appear here within 15 seconds.</p>
    </div>
  ) : (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>Employee</th>
            <th>Dept / Position</th>
            <th>Check in</th>
            <th>Check out</th>
            <th>Hours on site</th>
            <th className="right">Scans</th>
            <th>Branch</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const hours = diffHours(r.check_in || r.first_scan, r.check_out || (r.scans > 1 ? r.last_scan : null));
            return (
              <tr key={r.key}>
                <td>
                  {r.linked
                    ? <span className="strong">{r.name}</span>
                    : <span className="badge badge-red no-dot">Unlinked · id {r.device_user_id}</span>}
                </td>
                <td>
                  {r.department && <div style={{ fontSize: 13 }}>{r.department}</div>}
                  {r.position   && <div className="muted" style={{ fontSize: 12 }}>{r.position}</div>}
                </td>
                <td className="strong">{fmt12(r.check_in  || r.first_scan)}</td>
                <td className="muted">{r.check_out ? fmt12(r.check_out) : r.scans > 1 ? <span className="muted">{fmt12(r.last_scan)}</span> : <span className="muted">—</span>}</td>
                <td>
                  {hours
                    ? <span className="badge badge-green" style={{ fontFamily: 'var(--font-mono)' }}>{hours}</span>
                    : <span className="muted">—</span>}
                </td>
                <td className="right num">{fmtNumber(r.scans)}</td>
                <td className="muted">{r.branch || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  )
);

// ─── All scans table ──────────────────────────────────────────
const ScansTable = ({ rows }) => (
  rows.length === 0 ? (
    <div className="empty-state">
      <div className="ic">👉</div>
      <h4>No scans yet for this day</h4>
      <p>Once someone taps in on a fingerprint terminal, their punch will show up here — usually within 15 seconds.</p>
    </div>
  ) : (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>Time</th>
            <th>Employee</th>
            <th>Dept</th>
            <th>Branch</th>
            <th>Device</th>
            <th>Event</th>
            <th>Verify</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td className="strong">{new Date(s.event_time).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</td>
              <td>
                {(s.employee_id || s.user_id)
                  ? <span className="strong">{s.employee_name || s.display_name || s.user_name || s.resolved_user_name}</span>
                  : <span className="badge badge-red no-dot">Unlinked · id {s.device_user_id}</span>}
              </td>
              <td className="muted" style={{ fontSize: 12 }}>{s.department || '—'}</td>
              <td className="muted">{s.branch_name || '—'}</td>
              <td className="muted" style={{ fontSize: 12 }}>{s.device_name || s.device_serial}</td>
              <td>{EVENT_LABEL[s.event_type] || titleCase(String(s.event_type))}</td>
              <td className="muted" style={{ fontSize: 12 }}>{VERIFY_LABEL[s.verify_mode] || (s.verify_mode == null ? '—' : `#${s.verify_mode}`)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
);

// ─── Unlinked scans panel (now links to hr_employees) ─────────
const UnlinkedPanel = ({ rows, employees, onLinked, disabled }) => {
  const [pick, setPick] = useState({}); // { key: employee_id }
  const keyOf = (r) => `${r.device_serial}::${r.device_user_id}`;

  const submit = async (r) => {
    const employeeId = pick[keyOf(r)];
    if (!employeeId) return;
    try {
      await axios.post(attendanceLinkUrl, {
        device_serial:  r.device_serial,
        device_user_id: r.device_user_id,
        employee_id:    Number(employeeId),
      });
      toastSuccess('Linked — past scans updated');
      onLinked();
    } catch (err) { showAxiosError(err); }
  };

  if (rows.length === 0) {
    return (
      <div className="empty-state">
        <div className="ic">✅</div>
        <h4>Every scan is linked</h4>
        <p>All device IDs have been matched to registered employees.</p>
      </div>
    );
  }

  return (
    <>
      <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
        These device user IDs have scanned but are not linked to any employee yet.
        Match them below — or go to <strong>Employees</strong> and set the biometric ID there so future scans link automatically.
      </p>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Device</th>
              <th>Device user ID</th>
              <th className="right">Scans</th>
              <th>First seen</th>
              <th>Last seen</th>
              <th>Link to employee</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={keyOf(r)}>
                <td>
                  <div className="strong">{r.device_name || r.device_serial}</div>
                  {r.branch_name && <div className="muted" style={{ fontSize: 12 }}>{r.branch_name}</div>}
                </td>
                <td className="strong num">#{r.device_user_id}</td>
                <td className="right num">{fmtNumber(r.scans)}</td>
                <td className="muted">{fmtDate(r.first_seen_at)}</td>
                <td className="muted">{fmtDate(r.last_seen_at)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                      className="f-select"
                      value={pick[keyOf(r)] || ''}
                      onChange={(e) => setPick((p) => ({ ...p, [keyOf(r)]: e.target.value }))}
                      disabled={disabled}
                      style={{ minWidth: 220 }}
                    >
                      <option value="">— Choose employee —</option>
                      {employees.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.full_name}{e.department ? ` · ${e.department}` : ''}
                        </option>
                      ))}
                      {employees.length === 0 && <option disabled>No employees registered — go to Employees first</option>}
                    </select>
                    <button
                      className="btn-primary btn-sm"
                      disabled={disabled || !pick[keyOf(r)]}
                      onClick={() => submit(r)}
                    >Link</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ─── Devices panel ─────────────────────────────────────────────
const DevicesPanel = ({ rows, onRefresh, branches, isManager }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ serial_number: '', name: '', location: '', branch_id: '', shared_secret: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const generateSecret = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$';
    return Array.from({ length: 32 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.serial_number.trim()) { setErr('Serial number is required'); return; }
    if (!form.shared_secret.trim()) { setErr('Shared secret is required'); return; }
    setSaving(true);
    try {
      await axios.post(attendanceDevicesUrl, {
        serial_number:  form.serial_number.trim(),
        name:           form.name.trim() || null,
        location:       form.location.trim() || null,
        branch_id:      form.branch_id ? Number(form.branch_id) : null,
        shared_secret:  form.shared_secret.trim(),
      });
      setShowForm(false);
      setForm({ serial_number: '', name: '', location: '', branch_id: '', shared_secret: '' });
      onRefresh();
    } catch (error) {
      setErr(error.response?.data?.message || 'Failed to register device');
    } finally { setSaving(false); }
  };

  return (
    <>
      {isManager && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button className="btn-primary" onClick={() => { setShowForm((v) => !v); setErr(null); }}>
            {showForm ? 'Cancel' : '+ Register device'}
          </button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', padding: 20, marginBottom: 18 }}>
          <h4 style={{ margin: '0 0 14px', fontSize: 15 }}>Register fingerprint terminal</h4>
          {err && <div className="notice" style={{ marginBottom: 12 }}>{err}</div>}
          <div className="f-grid f-grid-2">
            <div className="f-row">
              <label className="f-label">Serial number *</label>
              <input className="f-input" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} placeholder="e.g. QME2254300013" />
            </div>
            <div className="f-row">
              <label className="f-label">Device name</label>
              <input className="f-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Main Entrance" />
            </div>
            <div className="f-row">
              <label className="f-label">Location</label>
              <input className="f-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Ground floor" />
            </div>
            <div className="f-row">
              <label className="f-label">Branch</label>
              <select className="f-select" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
                <option value="">— Any branch —</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="f-row" style={{ gridColumn: '1 / -1' }}>
              <label className="f-label">Shared secret * <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--mute)' }}>(the bridge uses this to authenticate — keep it private)</span></label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="f-input" value={form.shared_secret} onChange={(e) => setForm({ ...form, shared_secret: e.target.value })} placeholder="Strong random string" style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} />
                <button type="button" className="btn-ghost" onClick={() => setForm({ ...form, shared_secret: generateSecret() })}>Generate</button>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Registering…' : 'Register device'}</button>
          </div>
        </form>
      )}

      {rows.length === 0 ? (
        <div className="empty-state">
          <div className="ic">🛰️</div>
          <h4>No fingerprint devices registered yet</h4>
          <p>Click <strong>+ Register device</strong> above to add your fingerprint terminal. You'll need the device's serial number from the machine's settings menu.</p>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr><th>Name</th><th>Serial</th><th>Branch</th><th>Last seen</th><th>Status</th></tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const online = d.last_seen_at && (Date.now() - new Date(d.last_seen_at).getTime()) < 5 * 60 * 1000;
                return (
                  <tr key={d.id}>
                    <td>
                      <div className="strong">{d.name || '—'}</div>
                      {d.location && <div className="muted" style={{ fontSize: 12 }}>{d.location}</div>}
                    </td>
                    <td className="muted" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{d.serial_number}</td>
                    <td>{d.branch_name || <span className="muted">Any</span>}</td>
                    <td className="muted">{d.last_seen_at ? fmtDate(d.last_seen_at) : 'Never'}</td>
                    <td>
                      {!d.is_active ? <span className="badge badge-red no-dot">Revoked</span>
                        : online    ? <span className="badge badge-green">Online</span>
                                    : <span className="badge badge-gold">Offline</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

const Chip = ({ val, warn }) => (
  <span style={{
    marginLeft: 6, padding: '1px 8px', borderRadius: 999, fontSize: 11,
    background: warn ? 'var(--warn-soft)' : 'var(--bg-tint)',
    color:      warn ? 'var(--warn)'      : undefined,
  }}>{val}</span>
);

const KpiCard = ({ label, val, hint, tone, icon }) => (
  <div className="stat">
    <div className="lbl">{label}</div>
    <div className="val">{val}</div>
    <div className="hint">{hint}</div>
    <div className="ic" style={
      tone === 'brand'   ? { background: 'var(--brand-soft)',   color: 'var(--brand)' }
    : tone === 'accent'  ? { background: 'var(--accent-soft)',  color: 'var(--accent)' }
    : tone === 'warn'    ? { background: 'var(--warn-soft)',    color: 'var(--warn)' }
    : tone === 'success' ? { background: 'var(--success-soft)', color: 'var(--success)' }
    : {}}>{icon}</div>
  </div>
);

export default Attendance;
